import { Injectable, NgZone, OnDestroy } from '@angular/core';

/**
 * Controls how an overlay responds when the page is scrolled while it is open.
 *
 * - `'follow'` – The overlay repositions itself to stay anchored to its trigger element.
 * - `'close'` – The overlay closes immediately when any scroll is detected.
 * - `'none'`  – The overlay ignores scroll events (legacy / no-op behaviour).
 */
export type OverlayScrollBehavior = 'follow' | 'close' | 'none';

export interface OverlayScrollRegistration {
  behavior: OverlayScrollBehavior;
  /** Called on scroll when behavior === 'follow'. Should recalculate the overlay position. */
  onFollow: () => void;
  /** Called on scroll when behavior === 'close'. Should close the overlay. */
  onClose: () => void;
  /**
   * When provided, scroll events whose `target` is contained within the returned
   * element are ignored for this registration. Use this to prevent scrolls inside
   * a portaled dropdown container from closing or repositioning the overlay.
   * A factory function is used so the element can be resolved at event time
   * (e.g. the portal container may not exist at registration time).
   */
  ignoreScrollContainer?: () => Element | null | undefined;
}

/**
 * Service that coordinates scroll-aware behaviour for overlay components
 * (Select, TimePicker, DatePicker, etc.).
 *
 * Components register themselves when their overlay opens and unregister
 * when it closes or the component is destroyed. A single capture-phase
 * document scroll listener is shared across all active registrations,
 * so there is no per-component listener overhead.
 */
@Injectable({ providedIn: 'root' })
export class OverlayScrollService implements OnDestroy {
  private readonly registrations = new Map<symbol, OverlayScrollRegistration>();
  private scrollHandler: ((event: Event) => void) | null = null;
  /**
   * rAF ID for coalescing multiple 'follow' position updates within the same frame.
   * Only one rAF is scheduled at a time; subsequent scroll events within the same
   * frame cancel-and-reschedule instead of stacking.
   */
  private _pendingFollowRaf: number | null = null;

  constructor(private readonly ngZone: NgZone) {}

  /**
   * Registers an overlay to respond to scroll events while it is open.
   * @returns A unique symbol used as the registration ID. Pass it to `unregister()` when closing.
   */
  register(registration: OverlayScrollRegistration): symbol {
    const id = Symbol();
    this.registrations.set(id, registration);
    if (this.registrations.size === 1) {
      this.attachScrollListener();
    }
    return id;
  }

  /**
   * Removes an overlay from scroll tracking.
   * Must be called when the overlay closes or the component is destroyed.
   */
  unregister(id: symbol): void {
    this.registrations.delete(id);
    if (this.registrations.size === 0) {
      this.detachScrollListener();
    }
  }

  ngOnDestroy(): void {
    this.detachScrollListener();
    if (this._pendingFollowRaf !== null) {
      cancelAnimationFrame(this._pendingFollowRaf);
      this._pendingFollowRaf = null;
    }
  }

  private attachScrollListener(): void {
    // Run the listener outside Angular's zone to avoid triggering global
    // change detection on every scroll event. Each behavior path re-enters
    // the zone explicitly so only the necessary CD cycles happen.
    this.ngZone.runOutsideAngular(() => {
      this.scrollHandler = (event: Event) => this.handleScroll(event);
      document.addEventListener('scroll', this.scrollHandler, {
        capture: true, // Catches scroll on any scrollable element, not just window
        passive: true
      });
    });
  }

  private detachScrollListener(): void {
    if (this.scrollHandler) {
      document.removeEventListener('scroll', this.scrollHandler, {
        capture: true
      });
      this.scrollHandler = null;
    }
  }

  private handleScroll(event: Event): void {
    const scrollTarget = event.target as Element;
    // Snapshot entries before iterating — a 'close' callback may call unregister(),
    // which would mutate the map during iteration.
    const entries = Array.from(this.registrations.entries());

    // 'close' — run immediately inside Angular's zone so the overlay is removed
    // before the browser paints the next scrolled frame.
    // Skip any registration whose ignoreScrollContainer contains the scroll target
    // (e.g. the user scrolling inside a portaled dropdown must not close it).
    const closeEntries = entries.filter(([, r]) => {
      if (r.behavior !== 'close') return false;
      const ignore = r.ignoreScrollContainer?.();
      return !ignore || !ignore.contains(scrollTarget);
    });
    if (closeEntries.length > 0) {
      this.ngZone.run(() => {
        for (const [, reg] of closeEntries) {
          reg.onClose();
        }
      });
    }

    // 'follow' — coalesce multiple scroll events within the same animation frame,
    // then re-enter Angular's zone right before the browser paints so that
    // Angular's change detection runs and the repositioned style is committed
    // in the same frame as the new scroll position.
    const hasFollow = entries.some(([, r]) => r.behavior === 'follow');
    if (hasFollow) {
      if (this._pendingFollowRaf !== null) {
        cancelAnimationFrame(this._pendingFollowRaf);
      }
      this._pendingFollowRaf = requestAnimationFrame(() => {
        this._pendingFollowRaf = null;
        this.ngZone.run(() => {
          for (const [, reg] of this.registrations) {
            if (reg.behavior === 'follow') {
              reg.onFollow();
            }
          }
        });
      });
    }
  }
}
